import { apiClient } from '@/lib/apiClient';
import type { AbpPagedResponse } from '@/lib/gridDataSourceUtils';
import { entityDetailPath, entityListPath } from '@/lib/pageEntityContext';

/** The records of one entity filed under the open record: its list endpoint, and the field the record's id fills. */
export interface RelatedRecordsTarget {
  /** The target's generated EntityService route (/api/{module}/{entity}). */
  apiPath: string;
  /** Target module (lowercase) and entity, as entityListPath/entityDetailPath take them. */
  module: string;
  entity: string;
  field: string;
  value: string;
  /** The filter chip's text on the list. */
  label?: string;
}

/**
 * Where a related-records link lands (Related-Views-and-ReadOnly-Entities.md §5.1): the record itself when exactly one
 * is filed under the open record, else the target's list filtered on it. Asks the list endpoint with the fixed filter
 * the list view sends, for at most two rows. A failed lookup lands on the list, which reports its own error.
 */
export async function relatedRecordsPath(target: RelatedRecordsTarget): Promise<string> {
  const list = entityListPath(target.module, target.entity, { field: target.field, value: target.value, label: target.label });
  try {
    const { data } = await apiClient.get<AbpPagedResponse<{ id: string }>>(target.apiPath, {
      params: { skipCount: 0, maxResultCount: 2, [target.field]: target.value, [`${target.field}Operator`]: 'equals' },
    });
    const only = data.totalCount === 1 ? data.items[0]?.id : undefined;
    return only ? entityDetailPath(target.module, target.entity, only) : list;
  } catch {
    return list;
  }
}

/** Navigates to {@link relatedRecordsPath}. */
export async function openRelatedRecords(navigate: (path: string) => void, target: RelatedRecordsTarget): Promise<void> {
  navigate(await relatedRecordsPath(target));
}
